import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck, ExternalLink, Pencil, Plus, Search, Trash2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PageHeader from "@/components/PageHeader";
import GroupDialog from "@/components/GroupDialog";
import GroupImportExport from "@/components/GroupImportExport";
import { checkedLabel } from "@/components/GroupCheckCard";
import api, { errMsg } from "@/lib/api";

const STATUS_FILTER = {
  all: () => true,
  active: (g) => g.check_status === "active",
  inactive: (g) => g.check_status === "inactive",
  unchecked: (g) => !g.check_status,
};

export default function Groups() {
  const [params] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("all");
  const [status, setStatus] = useState(STATUS_FILTER[params.get("status")] ? params.get("status") : "all");
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const tags = useMemo(() => [...new Set(groups.map((g) => g.tag))], [groups]);
  const accounts = useMemo(() => [...new Set(groups.map((g) => g.fb_account).filter(Boolean))].sort(), [groups]);
  const [account, setAccount] = useState("all");
  const shown = groups.filter((g) => (tag === "all" || g.tag === tag) && (account === "all" || (g.fb_account || "") === (account === "none" ? "" : account)) && STATUS_FILTER[status](g) && g.name.toLowerCase().includes(q.toLowerCase()));

  const remove = async (g) => {
    if (!window.confirm(`Padam kumpulan "${g.name}"?`)) return;
    try { await api.delete(`/groups/${g.id}`); toast.success("Kumpulan dipadam"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Kumpulan FB"
        title="Senarai group"
        desc="Simpan group Facebook yang anda sertai. Salin pautan group dari pelayar (cth: https://www.facebook.com/groups/namagroup)."
        action={
          <div className="flex flex-wrap gap-2">
            <GroupImportExport onImported={load} />
            <Button variant="secondary" asChild data-testid="check-all-groups-btn"><Link to="/kumpulan/semak"><ClipboardCheck className="mr-2 h-4 w-4" /> Semak Semua</Link></Button>
            <Button onClick={() => setEditing({})} data-testid="add-group-btn"><Plus className="mr-2 h-4 w-4" /> Tambah Group</Button>
          </div>
        }
      />
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari group..." className="h-10 bg-card pl-9" data-testid="groups-search-input" />
        </div>
        <Select value={tag} onValueChange={setTag}>
          <SelectTrigger className="h-10 bg-card sm:w-48" data-testid="groups-filter-tag"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua kategori</SelectItem>
            {tags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-10 bg-card sm:w-48" data-testid="groups-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua status</SelectItem>
            <SelectItem value="active">Aktif</SelectItem>
            <SelectItem value="inactive">Tidak aktif</SelectItem>
            <SelectItem value="unchecked">Belum disemak</SelectItem>
          </SelectContent>
        </Select>
        <Select value={account} onValueChange={setAccount}>
          <SelectTrigger className="h-10 bg-card sm:w-48" data-testid="groups-filter-account"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua akaun FB</SelectItem>
            {accounts.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            <SelectItem value="none">Tiada akaun</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="panel divide-y divide-border overflow-hidden" data-testid="groups-list">
        {shown.length === 0 && <p className="p-10 text-center text-sm text-muted-foreground" data-testid="groups-empty">Tiada group. Tambah group pertama anda.</p>}
        {shown.map((g, i) => (
          <div key={g.id} className="fade-up flex flex-wrap items-center gap-4 px-5 py-4 transition-colors hover:bg-accent/40" style={{ animationDelay: `${i * 30}ms` }} data-testid={`group-item-row-${g.id}`}>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary"><Users className="h-4 w-4" /></div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{g.name}</p>
              <p className="truncate font-mono text-[11px] text-muted-foreground">{g.url}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground" data-testid={`group-checked-label-${g.id}`}>{checkedLabel(g)}</p>
            </div>
            <Badge variant="outline" className="border-border text-muted-foreground">{g.tag}</Badge>
            {g.fb_account && <Badge variant="outline" className="border-indigo-500/40 text-indigo-300" data-testid={`group-fb-account-badge-${g.id}`}>{g.fb_account}</Badge>}
            {g.needs_approval && <Badge variant="outline" className="border-amber-500/40 text-amber-300" data-testid={`group-needs-approval-badge-${g.id}`}>Perlu kelulusan</Badge>}
            {g.check_status === "inactive" && <Badge variant="outline" className="border-red-500/40 text-red-300" data-testid={`group-inactive-badge-${g.id}`}>Tidak aktif</Badge>}
            {g.check_status === "active" && <Badge variant="outline" className="border-emerald-500/40 text-emerald-300" data-testid={`group-active-badge-${g.id}`}>Aktif</Badge>}
            {g.is_sample && <Badge variant="outline" className="border-amber-500/30 text-amber-300">Contoh</Badge>}
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" asChild data-testid={`group-open-link-btn-${g.id}`}><a href={g.url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /></a></Button>
              <Button size="icon" variant="ghost" onClick={() => setEditing(g)} data-testid={`group-edit-btn-${g.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(g)} className="text-red-300 hover:text-red-200" data-testid={`group-delete-btn-${g.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      <GroupDialog group={editing} onClose={() => setEditing(null)} onSaved={load} accounts={accounts} />
    </div>
  );
}
